import { computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { usePermissions } from '@/composables/usePermissions';

// Cadence du polling du compteur de non-lues. Vingt secondes : c'est la même
// que le rafraîchissement du tableau de bord, et l'attente maximale avant de
// voir arriver une carte qu'on vient de vous confier ou un message qui vous
// cite. Le backoff protège le serveur quand l'endpoint tombe, pas la cadence
// nominale.
const POLL_INTERVAL = 20000;   // 20 s
const MAX_BACKOFF = 300000;    // 5 min

/**
 * Pilote le centre de notifications : polling du compteur (pause si onglet
 * caché, backoff exponentiel sur erreur) et cycle de vie. Le store ne fait que
 * l'état, ce composable orchestre.
 *
 * V1 interne only : la cloche est désactivée pour les clients extranet.
 * Migration temps réel future : remplacer le corps de poll() par un listener
 * Echo/Reverb — l'API publique de ce composable reste inchangée.
 */
export function useNotifications() {
    const store = useStore();
    const { isExtranetClient } = usePermissions();

    const enabled = computed(() => !isExtranetClient.value);
    const unreadCount = computed(() => store.getters.notifications_unread);

    let timer = null;
    let backoff = POLL_INTERVAL;
    let inFlight = false;

    const schedule = () => {
        clearTimeout(timer);
        if (!enabled.value || document.hidden) return;
        timer = setTimeout(poll, backoff);
    };

    const poll = async () => {
        if (inFlight) return; // évite les requêtes concurrentes (focus rapides)
        inFlight = true;
        try {
            // Les deux compteurs partent ensemble ; l'échec de la pastille ne
            // doit pas déclencher le backoff de la cloche (permission retirée
            // en cours de session, module coupé côté serveur…).
            await Promise.all([
                store.dispatch('notifications/fetchUnreadCount'),
                // Pastilles du menu : même cycle que la cloche, pas de second minuteur.
                store.dispatch('notifications/fetchMenuBadges').catch(() => {}),
            ]);
            backoff = POLL_INTERVAL;
        } catch (e) {
            backoff = Math.min(backoff * 2, MAX_BACKOFF);
        } finally {
            inFlight = false;
            schedule();
        }
    };

    const onVisibility = () => {
        if (document.hidden) {
            clearTimeout(timer);
        } else if (enabled.value) {
            poll(); // rafraîchissement immédiat au retour de focus
        }
    };

    onMounted(() => {
        if (!enabled.value) return;
        poll();
        document.addEventListener('visibilitychange', onVisibility);
    });

    onBeforeUnmount(() => {
        clearTimeout(timer);
        document.removeEventListener('visibilitychange', onVisibility);
    });

    // NOTE : ce composable installe un minuteur au montage. Pour rafraîchir une
    // pastille depuis un écran métier, ne PAS l'appeler (cela créerait un second
    // cycle de polling) — dispatcher directement
    // `notifications/fetchMenuBadges` sur le store.
    return {
        enabled,
        unreadCount,
        fetchList: (params) => store.dispatch('notifications/fetchList', params),
        markRead: (id) => store.dispatch('notifications/markRead', id),
        markUnread: (id) => store.dispatch('notifications/markUnread', id),
        markAllRead: () => store.dispatch('notifications/markAllRead'),
    };
}
